import m from "mithril";
import i18n from "./i18n/i18n";
import ModalManagerV2 from "./modal_v2";
import Modal_ConfirmV2 from "./components/modal_confirmV2";

/**
 * Warns the user before they lose unsaved work.
 *
 * A page registers a predicate with guard() and drops it again in onremove().
 * While any registered predicate reports dirty, both halves of navigation are
 * intercepted.
 *
 * In-app navigation wraps m.route.set and opens our own confirmation modal.
 * m.route.Link dispatches through the same property (mithril/api/router.js), so
 * link clicks are covered too.
 *
 * Browser back/forward never reaches m.route.set — Mithril resolves it from its
 * own popstate listener — so that path is intercepted separately, by undoing the
 * pop and asking before letting it through. See onPopState.
 *
 *
 * The confirmation modal renders through ModalManagerV2, which is only mounted in
 * the admin layout. Registering a guard from a public or account page would
 * block navigation without showing anything, so keep usage to admin pages.
 */

// key -> () => boolean
const guards = new Map();

let installed = false;
let originalRouteSet = null;

// Set while we are the ones calling history.back(), so our own handler lets
// that pop through instead of asking about it again.
let allowNextPop = false;
// Set while the modal for a popped entry is open, so holding down Back does not
// stack a modal per press.
let popConfirmOpen = false;

function isDirty() {
    let dirty = false;

    guards.forEach(function (check, key) {
        if (dirty)
            return;

        try {
            dirty = !!check();
        } catch (error) {
            // A broken predicate must never trap the user on the page.
            console.error("Unsaved-changes guard \"" + key + "\" threw", error);
        }
    });

    return dirty;
}

// onClose, when given, runs however the modal goes away: either button, the
// cross, or a click on the backdrop.
function confirmNavigation(proceed, onClose) {
    ModalManagerV2.open(
        i18n.t("you_have_unsaved_changes"),
        ["modal-mid"],
        Modal_ConfirmV2,
        {
            messageText: i18n.t(
                "unsaved_changes_warning"),
            cancelButtonLabel: i18n.t("go_back"),
            cancelButtonClasses: "button",
            cancelCallback: null,
            confirmButtonLabel: i18n.t("continue_without_saving"),
            confirmButtonClasses: "button.btn-error",
            confirmCallback: proceed
        },
        onClose || null
    );
    m.redraw();
}

function decodeSegment(segment) {
    try {
        return decodeURIComponent(segment);
    } catch (error) {
        return segment;
    }
}

// The route path for the current location, derived the way Mithril derives it
// (resolveRoute in mithril/api/router.js), so we agree with the router on what
// counts as the same route and on the URL to restore.
function locationPath() {
    let prefix = window.location.hash;

    if (m.route.prefix[0] !== "#") {
        prefix = window.location.search + prefix;

        if (m.route.prefix[0] !== "?") {
            prefix = window.location.pathname + prefix;

            if (prefix[0] !== "/")
                prefix = "/" + prefix;
        }
    }

    return prefix
        .replace(/(?:%[a-f89][a-f0-9])+/gim, decodeSegment)
        .slice(m.route.prefix.length);
}

// Browser back and forward. Mithril listens for popstate too, but it only
// schedules resolveRoute on a timer, so this handler always runs first no matter
// which listener was registered first — the guarded page is still mounted here
// and m.route.get() is still the path we are guarding.
function onPopState() {
    if (allowNextPop) {
        allowNextPop = false;
        return;
    }

    if (!isDirty())
        return;

    const guardedPath = m.route.get();

    // Something moved within the same route: no work is at risk.
    if (locationPath() === guardedPath)
        return;

    // Undo the pop before Mithril's pending resolveRoute reads the location.
    // This lands a fresh entry for the guarded route directly on top of the one
    // the user popped to, which does two things: resolveRoute finds the URL
    // unchanged and re-renders the page we are on, and the entry the user asked
    // for is now exactly one step back.
    window.history.pushState(null, "", m.route.prefix + guardedPath);

    // Repeated presses keep restoring the URL, but only one modal.
    if (popConfirmOpen)
        return;

    popConfirmOpen = true;
    confirmNavigation(
        function () {
            allowNextPop = true;
            window.history.back();
        },
        function () {
            popConfirmOpen = false;
        }
    );
}

function install() {
    if (installed) {
        return;
    }

    installed = true;

    // Left bound for the life of the page: the handler is a no-op while nothing
    // is dirty, and guards come and go with every page that registers one.
    window.addEventListener("popstate", onPopState);

    originalRouteSet = m.route.set;
    m.route.set = function (path, data, options) {
        if (!isDirty()) {
            return originalRouteSet.call(m.route, path, data, options);
        }
        confirmNavigation(function () {
            originalRouteSet.call(m.route, path, data, options);
        });
    };
}

const UnsavedChanges = {
    // isDirtyCheck is polled, so it always sees current state.
    guard: function (key, isDirtyCheck) {
        install();
        guards.set(key, isDirtyCheck);
    },

    release: function (key) {
        guards.delete(key);
    },

    // Drops every guard, so the rest of the navigation goes through unprompted.
    // For transitions that end the session — logging out — once the caller has
    // already confirmed the loss: the modal should not ask a second time on the
    // way to the login page.
    releaseAll: function () {
        guards.clear();
    },

    // Opens the same confirmation used for in-app navigation. For transitions
    // this module cannot see, such as switching tabs within a page, where the
    // caller decides when work is at risk.
    confirmDiscard: confirmNavigation,

    isDirty: isDirty
};

export default UnsavedChanges;
